/**
 * The audio math for a line by name (shared/voicelines.ts): cutting one line out of its take,
 * trimming and fading it, matching its loudness to the shipped lines, and writing and reading the
 * WAV it is kept as. The same rules as scripts/make-peon-voice.py, which does this with numpy and
 * ffmpeg at build time (docs/voice.md "Rendering the lines"); here it runs in the server, which has
 * neither, on the take's raw PCM. The apps read a kept WAV back with readWav to draw the mouth
 * (shared/peonEnvelope.ts).
 */

/** The character timing ElevenLabs returns with a take (`normalized_alignment` or `alignment`). */
export interface Alignment {
  characters: string[];
  character_start_times_seconds: number[];
  character_end_times_seconds: number[];
}

/** The shipped lines' median speech level (dBFS, scripts/make-peon-voice.py speech_level over public/audio/peons-original, 2026-10-28). */
export const TARGET_SPEECH_DB = -19.17;
/** The highest a matched line may peak (-1 dBFS), as the script caps it. */
const PEAK_CAP = 0.89;
/** -50 dB: the trim threshold; -40 dB lost the ends of words. */
const SILENCE = 10 ** (-50 / 20);
/** Air left before the first sound and after the last, and the fades over them. */
const HEAD_AIR_S = 0.04;
const TAIL_AIR_S = 0.08;
const FADE_IN_S = 0.005;
const FADE_OUT_S = 0.025;

/** Little-endian signed 16-bit PCM (ElevenLabs' `pcm_*` formats) as floats in [-1, 1). */
export function pcm16ToFloat(bytes: Uint8Array): Float32Array {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(Math.floor(bytes.byteLength / 2));
  for (let i = 0; i < out.length; i++) out[i] = view.getInt16(i * 2, true) / 32768;
  return out;
}

/** Where `texts` sit in the alignment's characters, in order: [first, last] character index of each, or null when one is missing. */
export function locateTexts(alignment: Alignment, texts: readonly string[]): [number, number][] | null {
  const chars = alignment.characters.join("");
  const ranges: [number, number][] = [];
  let pos = 0;
  for (const text of texts) {
    const at = chars.indexOf(text, pos);
    if (at < 0) return null;
    ranges.push([at, at + text.length - 1]);
    pos = at + text.length;
  }
  return ranges;
}

/** The middle of the quietest 30 ms window between `from` and `to` seconds (the script's `quietest`). */
export function quietestPoint(samples: Float32Array, sampleRate: number, from: number, to: number): number {
  const win = Math.max(3, Math.floor(0.03 * sampleRate));
  const step = Math.max(1, Math.floor(win / 3));
  const lo = Math.floor(Math.max(0, from) * sampleRate);
  const hi = Math.min(samples.length, Math.floor(to * sampleRate));
  let best = Infinity;
  let bestAt = (from + to) / 2;
  for (let i0 = lo; i0 < Math.max(lo + 1, hi - win); i0 += step) {
    let energy = 0;
    const end = Math.min(samples.length, i0 + win);
    for (let i = i0; i < end; i++) energy += samples[i] * samples[i];
    energy /= Math.max(1, end - i0);
    if (energy < best) {
      best = energy;
      bestAt = (i0 + win / 2) / sampleRate;
    }
  }
  return bestAt;
}

/**
 * Where the take is cut after `line`: the quietest point no earlier than 120 ms past the line's
 * last character (alignment ends early on soft trailing consonants; a cut there clipped "here" and
 * "waiting") and before the next text has properly begun. Null when the alignment does not hold
 * both texts.
 */
export function cutAfter(samples: Float32Array, sampleRate: number, alignment: Alignment, line: string, next: string): number | null {
  const ranges = locateTexts(alignment, [line, next]);
  if (!ranges) return null;
  const lineEnd = alignment.character_end_times_seconds[ranges[0][1]];
  const nextStart = alignment.character_start_times_seconds[ranges[1][0]];
  if (!Number.isFinite(lineEnd) || !Number.isFinite(nextStart)) return null;
  return quietestPoint(samples, sampleRate, lineEnd + 0.12, Math.max(lineEnd + 0.3, nextStart + 0.2));
}

/** RMS over 20 ms windows, as ffmpeg's silenceremove detects; true where a window is above -50 dB. */
function loudWindows(samples: Float32Array, sampleRate: number): { window: number; loud: boolean[] } {
  const window = Math.max(1, Math.round(0.02 * sampleRate));
  const loud: boolean[] = [];
  for (let i0 = 0; i0 < samples.length; i0 += window) {
    let sum = 0;
    const end = Math.min(samples.length, i0 + window);
    for (let i = i0; i < end; i++) sum += samples[i] * samples[i];
    loud.push(Math.sqrt(sum / (end - i0)) > SILENCE);
  }
  return { window, loud };
}

/**
 * Trims silence below -50 dB from both ends, leaving 40 ms of air before and 80 ms after, then
 * fades in over 5 ms and out over 25 ms (the script's silenceremove and afade chain). An all-quiet
 * clip comes back empty.
 */
export function trimAndFade(samples: Float32Array, sampleRate: number): Float32Array {
  const { window, loud } = loudWindows(samples, sampleRate);
  const first = loud.indexOf(true);
  const last = loud.lastIndexOf(true);
  if (first < 0) return new Float32Array(0);
  const start = Math.max(0, first * window - Math.round(HEAD_AIR_S * sampleRate));
  const end = Math.min(samples.length, (last + 1) * window + Math.round(TAIL_AIR_S * sampleRate));
  const out = samples.slice(start, end);
  const fadeIn = Math.min(out.length, Math.round(FADE_IN_S * sampleRate));
  for (let i = 0; i < fadeIn; i++) out[i] *= i / fadeIn;
  const fadeOut = Math.min(out.length, Math.round(FADE_OUT_S * sampleRate));
  for (let i = 0; i < fadeOut; i++) out[out.length - 1 - i] *= i / fadeOut;
  return out;
}

/**
 * The script's speech_level: RMS of the windows louder than a tenth of the loudest, in dBFS, over
 * windows of 1024 samples at 44.1 kHz (the same 23 ms at any rate). -Infinity for silence.
 */
export function speechLevel(samples: Float32Array, sampleRate: number): number {
  const frame = Math.max(1, Math.round((1024 * sampleRate) / 44100));
  const rms: number[] = [];
  for (let i0 = 0; i0 + frame <= samples.length; i0 += frame) {
    let sum = 0;
    for (let i = i0; i < i0 + frame; i++) sum += samples[i] * samples[i];
    rms.push(Math.sqrt(sum / frame));
  }
  const max = Math.max(0, ...rms);
  const loud = rms.filter((r) => r > max * 0.1);
  if (max === 0 || loud.length === 0) return -Infinity;
  return 20 * Math.log10(Math.sqrt(loud.reduce((s, r) => s + r * r, 0) / loud.length));
}

/** Plain gain to TARGET_SPEECH_DB, capped so the peak stays under -1 dBFS (no compressor: it made one-second clips worse). */
export function matchLoudness(samples: Float32Array, sampleRate: number, target = TARGET_SPEECH_DB): Float32Array {
  const level = speechLevel(samples, sampleRate);
  if (!Number.isFinite(level)) return samples.slice();
  let gain = 10 ** ((target - level) / 20);
  let peak = 0;
  for (const s of samples) peak = Math.max(peak, Math.abs(s));
  if (peak * gain > PEAK_CAP) gain = PEAK_CAP / peak;
  return samples.map((s) => s * gain);
}

/** A mono 16-bit PCM WAV of `samples`. */
export function writeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const bytes = new Uint8Array(44 + samples.length * 2);
  const view = new DataView(bytes.buffer);
  const ascii = (at: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(at + i, text.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, "data");
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, Math.round(s < 0 ? s * 32768 : s * 32767), true);
  }
  return bytes;
}

/** The first channel of a 16-bit PCM WAV as floats, with its rate; null for anything else. */
export function readWav(bytes: Uint8Array): { samples: Float32Array; sampleRate: number } | null {
  if (bytes.byteLength < 12) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = (at: number) => String.fromCharCode(view.getUint8(at), view.getUint8(at + 1), view.getUint8(at + 2), view.getUint8(at + 3));
  if (tag(0) !== "RIFF" || tag(8) !== "WAVE") return null;
  let channels = 0;
  let sampleRate = 0;
  let bits = 0;
  let format = 0;
  for (let at = 12; at + 8 <= bytes.byteLength; ) {
    const id = tag(at);
    const size = view.getUint32(at + 4, true);
    const body = at + 8;
    if (id === "fmt " && size >= 16) {
      format = view.getUint16(body, true);
      channels = view.getUint16(body + 2, true);
      sampleRate = view.getUint32(body + 4, true);
      bits = view.getUint16(body + 14, true);
    } else if (id === "data") {
      if (format !== 1 || bits !== 16 || channels < 1 || sampleRate <= 0) return null;
      const end = Math.min(bytes.byteLength, body + size);
      const frames = Math.floor((end - body) / (2 * channels));
      const samples = new Float32Array(frames);
      for (let i = 0; i < frames; i++) samples[i] = view.getInt16(body + i * 2 * channels, true) / 32768;
      return { samples, sampleRate };
    }
    at = body + size + (size % 2);
  }
  return null;
}

/**
 * One line's take made into its clip: cut before the throwaway, trimmed, faded and matched, as a
 * WAV. Null when the take's alignment does not hold the line and the throwaway, or nothing audible
 * is left; the caller then keeps nothing and tries again on a later ask.
 */
export function clipFromTake(pcm: Uint8Array, sampleRate: number, alignment: Alignment, line: string, throwaway: string): Uint8Array | null {
  const take = pcm16ToFloat(pcm);
  const cut = cutAfter(take, sampleRate, alignment, line, throwaway);
  if (cut === null) return null;
  const clip = trimAndFade(take.subarray(0, Math.round(cut * sampleRate)), sampleRate);
  if (clip.length < sampleRate * 0.2) return null;
  return writeWav(matchLoudness(clip, sampleRate), sampleRate);
}
